/**
 * LocalStorage caching utilities with expiration support.
 * Provides consistent caching patterns across the application.
 */

const isDev = process.env.NODE_ENV === 'development';

/** Default cache expiration: 2 hours in milliseconds */
export const DEFAULT_CACHE_EXPIRATION = 2 * 60 * 60 * 1000;

/** Short cache expiration: 12 hours in milliseconds */
export const LONG_CACHE_EXPIRATION = 12 * 60 * 60 * 1000;

/**
 * Get cached data from localStorage if not expired
 * @param {string} key - Cache key
 * @param {number} expiration - Expiration time in ms (default: 2 hours)
 * @returns {*|null} Cached data or null if expired/missing
 */
export const getCachedData = (key, expiration = DEFAULT_CACHE_EXPIRATION, { persistInDev } = {}) => {
  if (isDev && !persistInDev) return null;
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < expiration) {
      return data;
    }

    // Expired - remove from storage
    localStorage.removeItem(key);
    return null;
  } catch (e) {
    console.error(`Error reading cache for key "${key}":`, e);
    return null;
  }
};

/**
 * Set data in localStorage cache with timestamp
 * @param {string} key - Cache key
 * @param {*} data - Data to cache
 */
export const setCachedData = (key, data, { persistInDev } = {}) => {
  if (isDev && !persistInDev) return;
  try {
    const payload = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    console.error(`Error setting cache for key "${key}":`, e);
  }
};

/**
 * Remove cached data from localStorage
 * @param {string} key - Cache key
 */
export const removeCachedData = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error(`Error removing cache for key "${key}":`, e);
  }
};

/**
 * Check if cached data exists and is not expired
 * @param {string} key - Cache key
 * @param {number} expiration - Expiration time in ms
 * @returns {boolean} True if valid cache exists
 */
export const hasCachedData = (key, expiration = DEFAULT_CACHE_EXPIRATION) => {
  return getCachedData(key, expiration) !== null;
};

/**
 * Get or fetch data with automatic caching
 * @param {string} key - Cache key
 * @param {function} fetchFn - Async function to fetch data if not cached
 * @param {number} expiration - Expiration time in ms
 * @returns {Promise<*>} Cached or freshly fetched data
 */
export const getOrFetchCachedData = async (key, fetchFn, expiration = DEFAULT_CACHE_EXPIRATION) => {
  const cached = getCachedData(key, expiration);
  if (cached !== null) {
    return cached;
  }

  const data = await fetchFn();
  setCachedData(key, data);
  return data;
};

/**
 * Clear all cached data matching a prefix
 * @param {string} prefix - Key prefix to match
 */
export const clearCacheByPrefix = (prefix) => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.error(`Error clearing cache with prefix "${prefix}":`, e);
  }
};

/**
 * Clean localStorage items older than specified hours
 * Useful for periodic cleanup of stale data
 * @param {number} hours - Maximum age in hours
 */
export const cleanLocalStorageOlderThan = (hours) => {
  const maxAge = hours * 60 * 60 * 1000;
  const now = Date.now();

  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;

      try {
        const item = localStorage.getItem(key);
        if (!item) continue;

        const parsed = JSON.parse(item);
        if (parsed.timestamp && (now - parsed.timestamp > maxAge)) {
          localStorage.removeItem(key);
        }
      } catch {
        // Skip non-JSON items
      }
    }
  } catch (e) {
    console.error('Error cleaning localStorage:', e);
  }
};

// ============================================
// Cache Key Generators
// ============================================

/**
 * Generate cache key for compsheet data
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {string} Cache key
 */
export const getCompsheetCacheKey = (isPaid, variant = '') => {
  if (isPaid) return 'compsheetData';
  return variant ? `compsheetDataFree_${variant}` : 'compsheetDataFree';
};

/**
 * Generate cache key for acquisition snapshot compsheet data per ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} Cache key
 */
export const getAcquisitionCompsheetCacheKey = (ticker) => `acquisitionCompsheetData_${String(ticker || '').toUpperCase()}`;

/**
 * Generate cache key for financial data per ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} Cache key
 */
export const getFinancialCacheKey = (ticker) => `financialData_${ticker}`;

/**
 * Generate cache key for guidance vs actuals data per ticker
 * @param {string} ticker - Stock ticker symbol
 * @returns {string} Cache key
 */
export const getGuidanceCacheKey = (ticker) => `guidanceData_${String(ticker || '').toUpperCase()}`;

/**
 * Generate cache key for trended data
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {string} Cache key
 */
export const getTrendedCacheKey = (isPaid) => isPaid ? 'trendedData' : 'trendedDataFree';

/**
 * Generate cache key for developer metrics data
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {string} Cache key
 */
export const getDevmetricsCacheKey = (isPaid) => isPaid ? 'devmetricsData' : 'devmetricsDataFree';

/**
 * Generate cache key for job postings data
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {string} Cache key
 */
export const getJobsCacheKey = (isPaid) => isPaid ? 'jobDataQualityV4' : 'jobDataFreeQualityV4';

// ============================================
// Market Insights Cache Key Generators
// ============================================

/**
 * Generate cache key for index performance data
 * @returns {string} Cache key
 */
export const getIndexCacheKey = () => 'indexData';

/**
 * Generate cache key for index valuation data
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {string} Cache key
 */
export const getIndexValuationCacheKey = (isPaid) => isPaid ? 'indexValuationData' : 'indexValuationDataFree';

/**
 * Generate cache key for coverage data
 * @returns {string} Cache key
 */
export const getCoverageCacheKey = () => 'coverageData';

/**
 * Generate cache key for segmentation data
 * @returns {string} Cache key
 */
export const getSegmentationCacheKey = () => 'segmentationData';

/**
 * Generate cache key for growth curve data
 * @returns {string} Cache key
 */
export const getGrowthCurveCacheKey = () => 'growthcurve';

/**
 * Generate cache key for metrics trended data
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {string} Cache key
 */
export const getMetricsTrendedCacheKey = (isPaid) => isPaid ? 'metricsTrendedData' : 'metricsTrendedDataFree';
